import { catOf, type Front, type Part } from '@enercad/core';
import { api } from './api';

/* Client-side cache of the server parts database. */

const byPn = new Map<string, Part | null>();
const byType = new Map<string, Part[]>();
const loading = new Map<string, Promise<void>>();

const remember = (p: Part) => byPn.set(p.pn.toLowerCase(), p);

/** Part from the database, falling back to the built-in example catalogue. */
export const partOf = (pn: string): Part | undefined => (pn ? byPn.get(pn.toLowerCase()) ?? catOf(pn) : undefined);

export const partsForType = (type: string) => byType.get(type);

/** Every part of a symbol type (generators choose from the whole library, not the first page). */
export function loadTypeAll(type: string): Promise<void> {
  const key = 'all:' + type;
  let p = loading.get(key);
  if (!p) {
    p = api
      .parts('', type, 20000)
      .then((r) => {
        r.parts.forEach(remember);
        byType.set(type, r.parts);
      })
      .catch(() => void 0);
    loading.set(key, p);
  }
  return p;
}

export function loadType(type: string): Promise<void> {
  const key = 't:' + type;
  let p = loading.get(key);
  if (!p) {
    p = api
      .parts('', type, 500)
      .then((r) => {
        r.parts.forEach(remember);
        byType.set(type, r.parts);
      })
      .catch(() => void byType.set(type, []));
    loading.set(key, p);
  }
  return p;
}

/** Makes sure the given part numbers are cached (for checks and reports). */
export async function ensureParts(pns: string[]): Promise<void> {
  const missing = [...new Set(pns.filter((pn) => pn && !byPn.has(pn.toLowerCase())))];
  if (!missing.length) return;
  try {
    const { parts } = await api.lookupParts(missing);
    missing.forEach((pn) => byPn.set(pn.toLowerCase(), null));
    parts.forEach(remember);
    // accessories are listed (and priced) in the bill of materials too
    const acc = parts.flatMap((p) => (p.acc ?? []).map((a) => a.pn));
    if (acc.length) await ensureParts(acc);
  } catch {
    /* offline: the example catalogue still answers */
  }
}

const fronts = new Map<string, Front | null>();

export const frontOf = (pn: string): Front | undefined => (pn ? fronts.get(pn.toLowerCase()) ?? undefined : undefined);

/** Loads front views for these part numbers; resolves true when something new arrived. */
export async function ensureFronts(pns: string[]): Promise<boolean> {
  const missing = [...new Set(pns.filter((pn) => pn && !fronts.has(pn.toLowerCase())))];
  if (!missing.length) return false;
  missing.forEach((pn) => fronts.set(pn.toLowerCase(), null));
  try {
    const r = await api.fronts(missing);
    let any = false;
    for (const [pn, f] of Object.entries(r.fronts)) {
      fronts.set(pn.toLowerCase(), f);
      any = true;
    }
    return any;
  } catch {
    return false;
  }
}

export function invalidateParts() {
  fronts.clear();
  byPn.clear();
  byType.clear();
  loading.clear();
}
